import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import type { OfflineStore } from '../dataProvider';
import { trackingMethodLabel } from './offlineStoreModel';

/**
 * Offline stores list (D-offline-stores). Brick-and-mortar / card-linked-offer VENUES, the
 * descriptive-CRUD pattern (the Charities / PromoCode shape). Region-scoped by the access
 * SCOPE through the one marketsForScope mapping (a uk editor sees uk venues; per-venue
 * single market). A POSTCODE / ZIP search filter (an exact-match list filter, NOT a map and
 * NOT geocoding). Authoring gates stores:write; venues deactivate, never hard-delete.
 */

/** Active / inactive as a chip (the deactivate-not-delete flag). */
const StatusChip = () => {
  const record = useRecordContext<OfflineStore>();
  const active = record?.status === 'active';
  return (
    <Chip
      size="small"
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
};

const offlineFilters = [
  <TextInput key="q" source="q" label="Search name" alwaysOn resettable />,
  // The postcode / ZIP search: an exact-match list filter (NOT a map, NOT geocoding).
  <TextInput
    key="postcode"
    source="postcode"
    label="Postcode / ZIP"
    alwaysOn
    resettable
  />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'active', name: 'Active' },
      { id: 'inactive', name: 'Inactive' },
    ]}
  />,
];

const OfflineStoreListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'stores:write' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canWrite && <CreateButton label="New venue" />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const OfflineStoreListView = () => {
  const { region } = useRegion();
  const { role, accessibleRegions } = useAccessContext();
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'stores:write' },
  );
  return (
    <List
      resource="offlineStores"
      title="Offline stores"
      // Scope, not activeRegion: GLOBAL shows both markets (marketsForScope).
      filter={{ region }}
      filters={offlineFilters}
      actions={<OfflineStoreListActions />}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      {/* No row navigation and no bulk actions: no bulk delete, venues are
          deactivated never deleted. Edit is a per-row, capability-gated link. */}
      <Datagrid rowClick={false} bulkActionButtons={false}>
        <TextField source="name" label="Venue" />
        <TextField source="addressLine" label="Address" />
        <TextField source="postcode" label="Postcode / ZIP" />
        <FunctionField
          label="Market"
          render={(r: OfflineStore) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Tracking"
          render={(r: OfflineStore) => trackingMethodLabel(r.trackingMethod)}
        />
        <FunctionField
          label="IRL event"
          render={(r: OfflineStore) => (r.irlEvent ? 'Yes' : '')}
        />
        <FunctionField label="Status" render={() => <StatusChip />} />
        {canWrite && <EditButton />}
      </Datagrid>
    </List>
  );
};

/** Gated entry point registered as the `offlineStores` Resource's list. */
export const OfflineStoreList = () => (
  <RequireAccess capability="stores:read">
    <OfflineStoreListView />
  </RequireAccess>
);
